import type { ReactNode } from "react"
import Link from "@/v2-backoffice-client/components/link"
import type { UsageInsightMention } from "@/v2-backoffice-client/usage/scope/usage.scope.utils"

export type UsageInsightTextProps = {
  text: string
  /** Users named in `text` (exact substrings), in order of appearance. */
  mentions?: UsageInsightMention[]
  /**
   * Deep link per mention. Contexts differ: from a page use
   * `?v=user:<id>…`, from inside an open panel use `>>?v=user:<id>…` so the
   * user sheet pushes onto the stack instead of replacing it.
   */
  buildTo(mention: UsageInsightMention): string
}

// Insight sentence with the named users rendered as links to their user
// panel — "Didrik W. has gone quiet" becomes one click instead of a manual
// search. Plain-text copies (clipboard, tooltips) keep using the raw text.
function UsageInsightText(props: UsageInsightTextProps) {
  const { text, mentions, buildTo } = props

  if (!mentions || mentions.length === 0) return <>{text}</>

  const parts: ReactNode[] = []
  let rest = text
  for (const mention of mentions) {
    const index = rest.indexOf(mention.name)
    if (index === -1) continue
    if (index > 0) parts.push(rest.slice(0, index))
    parts.push(
      <Link
        key={`${mention.userId}-${parts.length}`}
        to={buildTo(mention)}
        iconSrc={null}
        variant="unstyled"
        className="text-foreground/90 underline decoration-muted-foreground/40 underline-offset-2 hover:text-foreground hover:decoration-current"
      >
        {mention.name}
      </Link>,
    )
    rest = rest.slice(index + mention.name.length)
  }
  parts.push(rest)

  return <>{parts}</>
}

export default UsageInsightText
